/**
 * Registration Constants
 *
 * Constants for the account registration flow including validation,
 * cooldowns, reCAPTCHA, industries, and languages.
 */

// Validation timing constants
export const EMAIL_VALIDATION_DEBOUNCE_MS = 500;
export const PHONE_VALIDATION_DEBOUNCE_MS = 500;

// Password requirements
export const MIN_PASSWORD_LENGTH = 8;

// SMS verification
export const SMS_CODE_LENGTH = 6;
export const SMS_RESEND_COOLDOWN_SEC = 60;

// reCAPTCHA
export const RECAPTCHA_ACTION = 'submitRegister';

// Disallowed characters for name fields (prevents XSS and other security issues)
export const DISALLOWED_CHARACTERS_PATTERN = /[<>{}[\]\\/]/g;

// Registration step identifiers
export const REGISTRATION_STEPS = {
    FORM: 'form',
    CHECK_EMAIL: 'check_email',
    VERIFY_SMS: 'verify_sms',
    SET_PASSWORD: 'set_password',
    COMPLETE: 'complete'
} as const;

export type RegistrationStep = typeof REGISTRATION_STEPS[keyof typeof REGISTRATION_STEPS];

// Industry values mapping - key is camelCase identifier, value is exact string for server
export const INDUSTRY_VALUES = {
    accountancy: 'Accountancy',
    adviceConsultancy: 'Advice & Consultancy',
    agriFood: 'Agri & Food',
    automotive: 'Automotive',
    construction: 'Construction',
    educationResearch: 'Education & Research',
    energy: 'Energy',
    financialServices: 'Financial Services',
    government: 'Government',
    healthPharma: 'Health & Pharma',
    housingCorporations: 'Housing Corporations',
    hrPayroll: 'HR & Payroll',
    it: 'IT',
    legalNotary: 'Legal & Notary',
    leisureTravel: 'Leisure & Travel',
    logisticsTransport: 'Logistics & Transport',
    manufacturing: 'Manufacturing',
    mediaEntertainment: 'Media & Entertainment',
    realEstate: 'Real Estate',
    retail: 'Retail',
    sport: 'Sport',
    utility: 'Utility',
    wasteProcessing: 'Waste Processing'
} as const;

export type IndustryKey = keyof typeof INDUSTRY_VALUES;
export type IndustryValue = typeof INDUSTRY_VALUES[IndustryKey];

// For dropdowns - use key for i18n: `esl.account_registration.industries.${key}`, value for server
export const REGISTRATION_INDUSTRIES = Object.entries(INDUSTRY_VALUES).map(([key, value]) => ({
    key,
    value
}));

// Language codes for registration
export const REGISTRATION_LANGUAGE_CODES = ['en', 'nl', 'fr', 'es', 'de', 'hu', 'it', 'da', 'pt'] as const;

export type RegistrationLanguageCode = typeof REGISTRATION_LANGUAGE_CODES[number];

// For dropdowns - code is both the value AND the i18n key suffix
// i18n key: `esl.account_registration.languages.${code}`
export const REGISTRATION_LANGUAGES = REGISTRATION_LANGUAGE_CODES.map(code => ({
    code,
    value: code
}));

// Country codes for registration (ISO 3166-1 alpha-2).
// Shared source of truth for both the Country dropdown and the phone-number
// country selector on the registration form (RFT-732).
export const REGISTRATION_COUNTRY_CODES = [
    'AT', 'AU', 'BE', 'BG', 'BQ', 'CA', 'CH', 'CW', 'CY', 'CZ',
    'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GB', 'GR', 'HR', 'HU',
    'IE', 'IS', 'IT', 'LT', 'LU', 'LV', 'MC', 'MT', 'NL', 'NO',
    'NZ', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'SX', 'UA', 'US'
] as const;

export type RegistrationCountryCode = typeof REGISTRATION_COUNTRY_CODES[number];

// For dropdowns - code is both the value AND the i18n key suffix
// i18n key: `esl.account_registration.countries.${code}`
export const REGISTRATION_COUNTRIES = REGISTRATION_COUNTRY_CODES.map(code => ({
    code,
    value: code
}));
